import type {AxiosError} from 'axios';
import axios from 'axios';
import {Form} from '@/plugins/FWForms/index';
import * as types from '../mutation-types';
import {performApiRequest} from '@/store/helpers/vuex-helpers';
import type {StoreLoaders} from '@/types/common';
import {handleApiError} from '@/utils/errorHandler';
import {push} from 'notivue'

export interface EmailTemplate {
    context: string;
    context_type: string;
    created_at: string;
    default: boolean;
    delivery_locations?: Array<{
        uuid: string;
        name: string;
        type?: string | null;
        active?: boolean;
    }>;
    email_body: string;
    email_body_blocks: {
        blocks: any[];
        time?: number;
        version?: string;
    };
    email_layout_id: string;
    identifier: string;
    is_automatic: boolean;
    is_scheduled: boolean;
    schedule_enabled: boolean;
    trigger_event: 'trip_start' | 'trip_end' | null;
    offset_days: number | null;
    send_hour: number | null;
    name: string;
    subject: string;
    updated_at: string;
    uuid: string;
}

export type EmailPreviewPayload = {
    order_id: string;
    subject: string;
    blocks: {
        blocks: unknown[];
        time?: number;
        version?: string;
    };
    recipients?: string[];
};

export type EmailPreview = {
    subject: string;
    html: string;
    recipient: string;
    from_name: string;
    from_email: string;
};

export type Email = {
    attachments: Array<any>,
    blocks: Array<any>,
    body: string,
    order_id: string,
    recipients: Array<string>,
    subject: string,
    template: {
        name: string,
        subject: string,
        identifier: string,
        uuid: string,
    } | null,
}

type EmailHistory = {
    created_at: string,
    email_body: string,
    email_full_body: string,
    email_layout_id: string,
    order_id: string,
    recipients: Array<string>,
    status: string,
    subject: string,
    updated_at: string,
    uuid: string,
}

export interface EmailState {
    loading: StoreLoaders,
    emailForm: Form<any>,
    history: EmailHistory[],
    emails: Array<any>,
    emailTemplates: EmailTemplate[],
    emailTemplate: EmailTemplate | null,
    email: Email | null,
}

const state: EmailState = {
    loading: {
        emails: false,
        createEmail: false,
        sendEmail: false,
        previewEmail: false,
        history: false,
        email_templates: false,
    },
    emailForm: new Form(),
    history: [],
    emails: [],
    emailTemplates: [],
    emailTemplate: null,
    email: null,
};

const getters = {
    loading: (state: EmailState) => state.loading,
    emailForm: (state: EmailState) => state.emailForm,
    emails: (state: EmailState) => state.emails,
    email: (state: EmailState) => state.email,
    history: (state: EmailState) => state.history,
    emailTemplates: (state: EmailState) => state.emailTemplates,
    emailTemplate: (state: EmailState) => state.emailTemplate,
};

const mutations = {
    [types.SET_EMAILS_LOADING](state: EmailState, loading: { subject: string, loading: boolean }) {
        state.loading[loading.subject] = loading.loading;
    },
    [types.SET_EMAILS](state: EmailState, emails: Array<any>) {
        state.emails = emails;
    },
    [types.SET_EMAIL_HISTORY](state: EmailState, emails: EmailHistory[]) {
        state.history = emails;
    },
    [types.SET_EMAIL_TEMPLATES](state: EmailState, templates: EmailTemplate[]) {
        state.emailTemplates = templates;
    },
    [types.PATCH_EMAIL_TEMPLATE](state: EmailState, template: EmailTemplate) {
        const index = state.emailTemplates.findIndex((item) => item.uuid === template.uuid);

        if (index === -1) {
            return;
        }

        state.emailTemplates[index] = {
            ...state.emailTemplates[index],
            ...template,
        };
    },
    [types.SET_EMAIL_TEMPLATE](state: EmailState, template: EmailTemplate) {
        state.emailTemplate = template;
    },
    [types.SET_EMAIL_FORM_ERROR](state: EmailState, error: string | Record<string, string[]>) {
        state.emailForm.errors.set(error);
    },
    [types.CLEAR_EMAIL_FORM_ERRORS](state: EmailState, form_field: string | null = null) {
        if (form_field === null || form_field === undefined) {
            state.emailForm.errors.clear();
        } else {
            state.emailForm.errors.clear(form_field);
        }
    },
};

const actions = {

    /**
     * Create an email.
     */
    async sendEmail({commit}, email: Email) {
        commit(types.CLEAR_EMAIL_FORM_ERRORS);

        try {
            const responseData = await performApiRequest(commit, 'SET_EMAILS_LOADING', 'sendEmail', () => axios.post('/v1/emails/send', email));

            return {email: responseData.email};
        } catch (error) {
            const err = error as AxiosError;
            const errors = (err?.response?.data as Record<string, string[]>)?.errors;
            const message = (err?.response?.data as { message?: string })?.message || 'There was an error';

            const firstError = errors ? Object.values(errors)[0]?.[0] : null;
            const errorMessage = firstError || message;

            commit(types.SET_EMAIL_FORM_ERROR, errors || message);

            return {error: errorMessage};
        }
    },

    async previewEmail({commit}, payload: EmailPreviewPayload): Promise<EmailPreview> {
        try {
            const responseData = await performApiRequest(commit, 'SET_EMAILS_LOADING', 'previewEmail', () => axios.post('/v1/emails/preview', payload));

            return responseData.preview;
        } catch (error) {
            handleApiError(error, 'There was an error loading the email preview');
            throw error;
        }
    },

    /**
     * Get email history
     */
    async history({commit}, data: { type: string, id: string }) {
        const params = new URLSearchParams(data).toString();

        try {
            const responseData = await performApiRequest(commit, 'SET_EMAILS_LOADING', 'history', () => axios.get(`/v1/emails/history?${params}`));
            commit(types.SET_EMAIL_HISTORY, responseData.emails || []);
        } catch (error) {
            handleApiError(error, 'There was an error fetching email history');
        }
    },

    /**
     * Get email templates
     */
    async getEmailTemplates({commit}, data: { context?: string } = {}) {
        const qString = data?.context ? `?context=${data.context}` : '';

        try {
            const responseData = await performApiRequest(commit, 'SET_EMAILS_LOADING', 'email_templates', () => axios.get(`/v1/email-templates${qString}`));
            commit('SET_EMAIL_TEMPLATES', responseData.emailTemplates || []);
        } catch (error) {
            handleApiError(error, 'There was an error fetching email templates');
        }
    },

    /**
     * Get email template
     */
    async getEmailTemplate({commit}, uuid: string) {
        const responseData = await performApiRequest(commit, 'SET_EMAILS_LOADING', 'email_templates', () => axios.get(`/v1/email-templates/${uuid}`));
        commit(types.SET_EMAIL_TEMPLATE, responseData.emailTemplate || {});
    },

    /**
     * Update email template
     */
    async updateEmailTemplate({commit}, emailTemplate: EmailTemplate) {
        try {
            const responseData = await performApiRequest(commit, 'SET_EMAILS_LOADING', 'email_templates', () => axios.put(`/v1/email-templates/${emailTemplate.uuid}`, emailTemplate));
            commit(types.SET_EMAIL_TEMPLATE, responseData.emailTemplate || {});
            return responseData.emailTemplate;
        } catch (error) {
            handleApiError(error, 'There was an error updating your email templates');
        }
    },

    /**
     * Reset email template
     */
    async resetEmailTemplate({commit}, emailTemplate: EmailTemplate) {
        const responseData = await performApiRequest(commit, 'SET_EMAILS_LOADING', 'email_templates', () => axios.post(`/v1/email-templates/${emailTemplate.uuid}/reset`));
        commit(types.SET_EMAIL_TEMPLATE, responseData.emailTemplate || {});

        push.success({
            message: responseData.message || 'Email template reset successfully',
        });
        return responseData.emailTemplate;
    },

    /**
     * Send test email template
     */
    async sendTestTemplateEmail({commit}, emailTemplate: EmailTemplate) {
        try {
            await performApiRequest(commit, 'SET_EMAILS_LOADING', 'email_templates', () => axios.post(`/v1/email-templates/${emailTemplate.uuid}/send-test`));
        } catch (error) {
            handleApiError(error);
        }
    },

    async addScheduledTemplate({commit}, uuid: string) {
        try {
            const responseData = await performApiRequest(commit, 'SET_EMAILS_LOADING', 'email_templates', () => axios.post(`/v1/email-templates/${uuid}/add`));
            return responseData.emailTemplate;
        } catch (error) {
            handleApiError(error, 'There was an error adding the scheduled email template');
        }
    },

    async toggleScheduledTemplate({commit}, uuid: string) {
        try {
            const responseData = await performApiRequest(commit, 'SET_EMAILS_LOADING', 'email_templates', () => axios.post(`/v1/email-templates/${uuid}/toggle-schedule`));
            push.success({
                message: responseData.message || 'Scheduled email template updated successfully',
            });
            return responseData.emailTemplate;
        } catch (error) {
            handleApiError(error, 'There was an error updating the scheduled email template');
        }
    },

    async updateScheduledTemplate({commit}, payload: Pick<EmailTemplate, 'uuid' | 'identifier' | 'trigger_event' | 'offset_days' | 'send_hour' | 'schedule_enabled'>) {
        try {
            const responseData = await performApiRequest(commit, 'SET_EMAILS_LOADING', 'email_templates', () => axios.put(`/v1/email-templates/${payload.uuid}`, {
                ...payload,
                schedule_only: true,
            }));
            return responseData.emailTemplate;
        } catch (error) {
            handleApiError(error, 'There was an error saving the email schedule');
        }
    },

    async duplicateScheduledTemplate({commit}, uuid: string) {
        try {
            const responseData = await performApiRequest(commit, 'SET_EMAILS_LOADING', 'email_templates', () => axios.post(`/v1/email-templates/${uuid}/duplicate`));
            push.success({
                message: responseData.message || 'Scheduled email template duplicated successfully',
            });
            return responseData.emailTemplate;
        } catch (error) {
            handleApiError(error, 'There was an error duplicating the scheduled email template');
        }
    },

    async renameScheduledTemplate({commit}, payload: { uuid: string; name: string }) {
        try {
            const responseData = await performApiRequest(commit, 'SET_EMAILS_LOADING', 'email_templates', () => axios.patch(`/v1/email-templates/${payload.uuid}/name`, {
                name: payload.name,
            }));

            if (responseData.emailTemplate) {
                commit(types.PATCH_EMAIL_TEMPLATE, responseData.emailTemplate);
            }

            push.success({
                message: responseData.message || 'Scheduled email template renamed successfully',
            });

            return responseData.emailTemplate;
        } catch (error) {
            handleApiError(error, 'There was an error renaming the automation');
            throw error;
        }
    },

    async updateEmailTemplateDeliveryLocations({commit}, payload: { uuid: string; delivery_locations: string[] }) {
        try {
            await performApiRequest(commit, 'SET_EMAILS_LOADING', 'email_templates', () => axios.put(`/v1/email-templates/${payload.uuid}/delivery-locations`, {
                delivery_locations: payload.delivery_locations,
            }));
            push.success({
                message: 'Delivery locations updated successfully',
            });
        } catch (error) {
            handleApiError(error, 'There was an error saving delivery locations for this automation');
        }
    },
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
};
